import React, { useContext, useEffect, useState } from 'react';

import {
  ProductPageTemplate,
  ContentGeneratorDrawer,
  PageGeneratorThemeProvider,
  useProductDefaultValue,
} from 'components/page-generators';
import DrawerContent from 'components/JsonApi/components/DrawerContent';

import {
  AccordionNameEnum,
  BundlesEnum,
  FormSubmitEnum,
  useEntities,
} from 'utils/drupalEntities';
import DropZoneErrorDialog from 'components/JsonApi/components/dialogs/DropZoneErrorDialog';
import { useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import SaveOptionsDialog from 'components/JsonApi/components/dialogs/SaveOptionsDialog';
import { backHomeLink } from 'utils/constants';
import { LoaderContext } from 'context/LoaderContextProvider';
import InfoDialog from 'components/JsonApi/components/dialogs/InfoDialog';

const ProductCreate = ({ jsonApiLang }) => {
  if (!jsonApiLang) return <></>;
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const defaultValue = useProductDefaultValue();
  const {
    listEntityFieldsForm,
    handleHeaderBtnClick,
    handleOpenDrawerClick,
    onSubmitData,
    setCustomErrors,
    setOpen,
    previewData,
    generalAccordionComponents,
    basicAccordionComponents,
    additionalContents,
    brandName,
    btnCliked,
    submittedData,
    customErrors,
    open,
  } = useEntities();
  const methods = useForm({ defaultValues: {} });
  const { getValues, setValue, control, watch, reset } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalContents',
  });
  const value = { ...defaultValue };
  const { setLoaderActive } = useContext(LoaderContext);
  //Loader Function
  const showLoader = () => setLoaderActive(true);

  // States
  const [errorDialog, setErrorDialog] = useState(false);
  const [infoDialog, setInfoDialog] = useState({
    isOpen: false,
    i18n: i18n.info.preview,
    img: '',
  });
  const [saveOptionsDialog, setSaveOptionsDialog] = useState({
    isOpen: false,
    i18n: i18n.info.save,
    options: [
      { value: '0', label: i18n.info.save.options.save },
      { value: '1', label: i18n.info.save.options.doNotSave },
    ],
  });

  // Effects
  useEffect(() => {
    setOpen(true);
    listEntityFieldsForm({
      locale: jsonApiLang,
      bundle: BundlesEnum.PRODUCT,
    });
    setErrorDialog({
      isOpen: true,
      i18n: i18n.info.language,
      requiredMembers: [],
    });
  }, []);

  useEffect(() => {
    if (btnCliked && submittedData) {
      switch (btnCliked) {
        case FormSubmitEnum.BACK:
          setSaveOptionsDialog({
            i18n: i18n.info.save,
            options: [
              { value: '0', label: i18n.info.save.options.save },
              { value: '1', label: i18n.info.save.options.doNotSave },
            ],
            isOpen: true,
          });
          break;
        case FormSubmitEnum.DRAFT:
          setErrorDialog({
            isOpen: true,
            i18n: i18n.info.beforseSave,
            requiredMembers: [],
          });
          break;
        case FormSubmitEnum.PUBLISH:
          setErrorDialog({
            isOpen: true,
            i18n: i18n.info.beforeApprovation,
            requiredMembers: [],
          });
          break;
        case FormSubmitEnum.PREVIEW:
          onSubmitData(btnCliked, jsonApiLang);
          break;
        default:
          break;
      }
    }
  }, [btnCliked, submittedData]);

  useEffect(() => {
    if (customErrors) {
      setErrorDialog({ isOpen: true, ...customErrors });
    }
  }, [customErrors]);

  // Functions
  const handleBackOptionSelected = () => {
    showLoader();
    const selectedOption = getValues('save') || '1';
    if (selectedOption === '0') onSubmitData(btnCliked, jsonApiLang);
    else
      window.location.href = `${backHomeLink}/${jsonApiLang}/admin/sdm/content`;
  };
  const handleSubmitLogic = () =>
    btnCliked && onSubmitData(btnCliked, jsonApiLang);

  // Renders
  const renderErrorDialog = () => {
    if (!errorDialog.isOpen) return null;
    return (
      <DropZoneErrorDialog
        onClose={() => {
          setErrorDialog({ isOpen: false, text: '' });
        }}
        i18n={{
          ...errorDialog.i18n,
          agree: 'Ok',
        }}
        onAccept={handleSubmitLogic}
        requiredMembers={errorDialog?.requiredMembers || []}
      />
    );
  };
  const renderInfoDialog = () => {
    if (!infoDialog.isOpen) return null;
    return (
      <InfoDialog
        onClose={() => {
          setInfoDialog((prev) => ({ ...prev, isOpen: false, img: '' }));
        }}
        i18n={{
          ...infoDialog.i18n,
          agree: 'Ok',
        }}
        img={infoDialog.img}
      />
    );
  };

  const renderSaveOptionsDialog = () => {
    if (!saveOptionsDialog.isOpen) return null;
    return (
      <SaveOptionsDialog
        onClose={() => {
          setSaveOptionsDialog((prev) => ({ ...prev, isOpen: false }));
        }}
        onAgree={() => handleBackOptionSelected()}
        i18n={{
          ...saveOptionsDialog.i18n,
        }}
        options={saveOptionsDialog?.options}
        control={control}
      />
    );
  };

  return (
    <PageGeneratorThemeProvider>
      {generalAccordionComponents?.length &&
      basicAccordionComponents?.length ? (
        <>
          <ProductPageTemplate {...value} {...previewData} {...brandName} />
          <ContentGeneratorDrawer
            open={open}
            onOpen={() => handleOpenDrawerClick(reset)}
            onBtnClicked={(btn) => handleHeaderBtnClick(btn, getValues())}
            hideLangBtn={true}
            hideApprovalBtn={true}
          >
            <DrawerContent
              generalData={generalAccordionComponents}
              basicData={basicAccordionComponents}
              additionalData={additionalContents}
              setCustomErrors={setCustomErrors}
              setValue={setValue}
              getValues={getValues}
              control={control}
              watch={watch}
              fields={fields}
              append={append}
              remove={remove}
              langCode={jsonApiLang}
              isBrand={false}
              productLangCode={'it'}
              openAccordions={[AccordionNameEnum.GENERAL]}
              setInfoDialog={setInfoDialog}
            />
          </ContentGeneratorDrawer>
          {renderErrorDialog()}
          {renderSaveOptionsDialog()}
          {renderInfoDialog()}
        </>
      ) : null}
    </PageGeneratorThemeProvider>
  );
};

export default ProductCreate;
